import { FC, useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { DiscountTypeEnum, waitAsync } from '@sweatpals/shared';
import { CoreLinkButton, Option } from '../../_core';
import { PromocodeCard } from './promocode-card.component';
import { DiscountsFormDto, DiscountTierEnum } from '../../../models/dto';
import { useDiscountsModalState } from '../hooks/use-discounts-modal-state.hook';
import { PlusIcon } from '../../../icons';
import { RightBlackModal } from '../../right-modal';
import { customResetForm, scrollToFirstErrorElement } from '../../../utils';

const defaultValues: DiscountsFormDto = {
	discounts: [
		{
			tiersType: DiscountTierEnum.All,
			type: DiscountTypeEnum.Fixed,
			amount: undefined as any,
			code: '',
			tiers: { all: true },
			withMaxUses: false,
			maxUses: null,
		},
	],
};

export interface PromocodesModalProps<TValues = DiscountsFormDto> {
	open: boolean;
	tiersOptions: Option[];
	data?: TValues;
	promocodesUsesById?: Record<string, number>;
	tiersLabel?: string;
	allTiersRadioLabel?: string;
	selectedTiersRadioLabel?: string;
	onClose?(isDirty?: boolean): void;
	onSubmit?(data: TValues): void;
}

export const PromocodesModal: FC<PromocodesModalProps> = props => {
	const {
		open,
		data = defaultValues,
		tiersOptions,
		promocodesUsesById,
		onClose,
		onSubmit,
		tiersLabel,
		allTiersRadioLabel,
		selectedTiersRadioLabel,
	} = props;

	const formRef = useRef<HTMLDivElement>(null);

	const handleSave = async (promocodes: DiscountsFormDto) => {
		await waitAsync(500);

		onSubmit?.(promocodes);
	};

	const { form: promocodesForm } = useDiscountsModalState({
		defaultValues: structuredClone(data),
		mode: 'onBlur',
	});

	const handleClose = useCallback(() => {
		onClose?.(promocodesForm.formState.isDirty);
	}, [onClose, promocodesForm]);

	useEffect(() => {
		if (open) {
			customResetForm(promocodesForm, structuredClone(data));
		}
	}, [open]);

	// console.debug('intakeForm', intakeForm);
	// console.debug('errors', errors);

	const {
		fields: discounts,
		append,
		remove,
	} = useFieldArray({
		name: `discounts`,
		control: promocodesForm.control,
		keyName: 'key',
	});

	const addField = useCallback((): void => {
		append({
			tiersType: DiscountTierEnum.All,
			type: DiscountTypeEnum.Fixed,
			amount: undefined as any,
			code: '',
			tiers: { all: true },
			withMaxUses: false,
			maxUses: null,
		});
	}, [append]);

	const removeField = useCallback(
		(index: number): void => {
			remove(index);
		},
		[remove]
	);

	const validateForm = useCallback(async (): Promise<void> => {
		await promocodesForm.trigger();

		scrollToFirstErrorElement(formRef.current);
	}, [promocodesForm]);

	return (
		<FormProvider {...promocodesForm}>
			<RightBlackModal
				open={open}
				onClose={handleClose}
				headerTitle='Promo codes'
				headerDescription='Add promo codes for price tiers'
				modalProps={{
					component: 'form',
					onSubmit: e => {
						// stop propagation to not trigger price tiers form
						e.stopPropagation();
						promocodesForm.handleSubmit(handleSave, validateForm)(e);
					},
				}}
				submitButtonProps={{
					type: 'submit',
					loading: promocodesForm.formState.isSubmitting,
				}}
				contentBoxRef={formRef}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '24px',
					}}
				>
					{discounts.map((discount, index) => (
						<PromocodeCard
							key={discount.key}
							index={index}
							tiersOptions={tiersOptions}
							promocodeUses={discount.id ? promocodesUsesById?.[discount.id] : undefined}
							tiersLabel={tiersLabel}
							allTiersRadioLabel={allTiersRadioLabel}
							selectedTiersRadioLabel={selectedTiersRadioLabel}
							onRemove={() => {
								removeField(index);
							}}
						/>
					))}
				</Box>
				<Box>
					<CoreLinkButton type="button" onClick={addField}>
						<PlusIcon width={16} height={16} />
						Add new promo code
					</CoreLinkButton>
				</Box>
			</RightBlackModal>
		</FormProvider>
	);
};
